<template>
  <div class="branch-opening">

    <template v-if="props.short">
      <div class="flex gap-4 items-center mb-8 pb-4 text-xl font-bold border-b border-neutral-200" :class="currentStatusColor">
        <AtomsImagesIcon icon="clock" />
        {{ currentStatus }}
      </div>
      <div v-if="openingData.information" class="text-lg font-bold text-error-500" v-html="openingData.information"></div>
    </template>

    <template v-else>

      <!-- header -->

      <div class="flex flex-wrap font-bold">

        <div class="w-full px-6 py-3 flex items-center justify-center border-l border-t border-r border-neutral-300 tablet:border-b tablet:border-r-0 tablet:flex-1">
          <AtomsImagesIcon icon="clock" class="mr-2" />
          <component :is="props.openingTimeComponentType ?? 'span'" class="text-lg tablet:flex-1">{{ $t('Otevírací doba') }}</component>
        </div>

        <div class="w-full px-6 py-3 text-lg text-center text-neutral-50 tablet:flex-1 tablet:min-w-[360px]" :class="currentStatusBg">{{ currentStatus }}</div>
      </div>

      <!-- details -->

      <div v-if="!openingData.closed || openingData.information" class="pt-4 pb-2 border-b border-neutral-300 tablet:px-4 tablet:border-l tablet:border-r">

        <div v-if="!openingData.closed" class="branch-opening__days">
          <div v-for="day in days" class="branch-opening__day" :class="{ 'branch-opening__day--closed': day.closed }">
            <div class="branch-opening__day__wrapper">
              <div class="branch-opening__day__label">
                {{ day.label }}
                <div class="branch-opening__day__label__date">{{ day.date }}</div>
              </div>
              <div class="branch-opening__day__times">
                <template v-if="!day.closed && day.times.some(time => time.from !== time.to)">
                  <div v-for="time in day.times.filter(time => time.from !== time.to)">{{ time.from }} - {{ time.to }}</div>
                </template>

                <template v-else>
                  <div>{{ $t('Zavřeno') }}</div>
                </template>
              </div>
            </div>
          </div>
        </div>

        <div v-if="openingData.information" class="branch-opening__information" v-html="openingData.information"></div>
      </div>
    </template>
  </div>

</template>

<script setup>

const props = defineProps({
  id: String,
  openingTimeComponentType: String,
  short: Boolean | undefined
});

const locale = useLocale().getLocale();
const i18n = useI18n();
const utils = useUtils();
const aUtils = useAvailabilityUtils();

const weekDays = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];
const weekDaysTranslations = ['Neděle', 'Pondělí', 'Úterý', 'Středa', 'Čtvrtek', 'Pátek', 'Sobota'];
const onWeekDaysTranslations = ['v neděli', 'v pondělí', 'v úterý', 've středu', 've čtvrtek', 'v pátek', 'v sobotu'];

const openingData = (await useApiFetch(`/api/branchOpening?branch=${props.id == '0' ? 'OLOMOUC' : (props.id == '1' ? 'SEMILY' : 'OSTRAVA')}&languageId=${locale.languageId}`)).custom.ta.branchOpening;

//openingData.closed = true;
//openingData.information = '<p>Prosím pozor - máme dočasně změněnou otevírací dobu! Trezorová místnost pouze po telefonické dohodě na telefonu + 420 774 923 869. Omlouváme se za komplikace a děkujeme za pochopení</p>';
//const days = [{ label: 'Dnes', date: '12.07.', closed: true, times: ['09:00 - 12:00', '12:30 - 17:00'] }, { label: 'Dnes', closed: true, date: '12.07.', times: ['09:00 - 12:00', '12:30 - 17:00'] }, 2, 3, 4, 5, 6];

//#region utils

const isBetween = (time, from, to) => {
  let fromArray = from.split(':');
  let toArray = to.split(':');
  let fromNum = parseInt(fromArray[0]) * 100 + parseInt(fromArray[1]);
  let toNum = parseInt(toArray[0]) * 100 + parseInt(toArray[1]);
  let timeNum = time.getHours() * 100 + time.getMinutes();

  return timeNum > fromNum && timeNum < toNum;
}

const isTodayNext = (time, from, to) => {
  let fromArray = from.split(':');
  let toArray = to.split(':');
  let fromNum = parseInt(fromArray[0]) * 100 + parseInt(fromArray[1]);
  let toNum = parseInt(toArray[0]) * 100 + parseInt(toArray[1]);
  let timeNum = time.getHours() * 100 + time.getMinutes();

  return fromNum > timeNum && toNum > timeNum;
}

const trimZero = (time) => {
  return time.replace(/^0+/, '');
}

//#endregion

const days = [];

let currentStatus;
let currentStatusBg;
let currentStatusColor;

if (!openingData.closed) {

  const today = utils.extractDate(new Date());

  for (let i = 0; i < 7; i++) {

    const date = utils.addDaysToDate(today, i);
    const basicOpeningTimes = openingData.basicOpeningTimes.find(dayOfWeekTimes => date.getDay() === weekDays.indexOf(dayOfWeekTimes.dayOfWeek));
    const customOpeningTimes = openingData.customOpeningTimes.find(customDay => utils.extractDate(new Date(customDay.date)).getTime() === date.getTime());

    let dayLabel;

    switch (i) {
      case 0:
        dayLabel = i18n.t('Dnes');
        break;
      case 1:
        dayLabel = i18n.t('Zítra');
        break;
      default:
        dayLabel = i18n.t(weekDaysTranslations[date.getDay()]);
        break;
    }

    days.push({
      label: dayLabel,
      rawDate: date,
      date: date.toLocaleDateString(locale.languageCode, { day: 'numeric', month: 'numeric' }).replaceAll(' ', ''),
      closed: customOpeningTimes?.isClosed ?? basicOpeningTimes?.isClosed,
      times: customOpeningTimes?.openingTimes?.length ? customOpeningTimes.openingTimes : basicOpeningTimes.openingTimes
    });
  }

  const now = new Date();

  let openingHours = days.find(day => {

    let isCurrentOrFutureDate = day.rawDate.getTime() >= today.getTime();
    let isOpen = !day.closed;
    let hasOpeningTimes = day.times !== null;

    let hasValidOpeningTimesToday = day.rawDate.getTime() === today.getTime() &&
      day.times.some(time => isBetween(now, time.from, time.to) || isTodayNext(now, time.from, time.to));

    let hasValidOpeningTimesFuture = day.rawDate.getTime() > today.getTime() &&
      day.times.some(time => time.from !== time.to);

    return isCurrentOrFutureDate && isOpen && hasOpeningTimes && (hasValidOpeningTimesToday || hasValidOpeningTimesFuture);
  });

  if (!openingHours) {
    currentStatus = i18n.t('Dočasně zavřeno');
    currentStatusBg = 'bg-error-500';
    currentStatusColor = 'text-error-500';
  }

  // today

  else if (openingHours.rawDate.getTime() === today.getTime()) {

    let openingTime = openingHours.times.find(time => isBetween(now, time.from, time.to));
    let openingTime2 = openingHours.times.find(time => isTodayNext(now, time.from, time.to));

    if (openingTime2 && openingTime) {
      currentStatus = i18n.t('Máme otevřeno do {0} a pak zase od {1}', [trimZero(openingTime.to), trimZero(openingTime2.from)]);
      currentStatusBg = 'bg-primary-500';
      currentStatusColor = 'text-primary-500';
    }
    else if (openingTime) {
      currentStatus = i18n.t('Máme otevřeno do {0}', [trimZero(openingTime.to)]);
      currentStatusBg = 'bg-primary-500';
      currentStatusColor = 'text-primary-500';
    }
    else if (openingTime2) {
      currentStatus = i18n.t('Máme otevřeno od {0}', [trimZero(openingTime2.from)]);
      currentStatusBg = 'bg-secondary-500';
      currentStatusColor = 'text-secondary-500';
    }

  }

  // tomorrow

  else if (openingHours.rawDate.getTime() === utils.addDaysToDate(today, 1).getTime()) {
    currentStatus = i18n.t('Otevřeno bude opět zítra v {0}', [trimZero(openingHours.times[0].from)]);
    currentStatusBg = 'bg-secondary-500';
    currentStatusColor = 'text-secondary-500';
  }

  // day after tomorrow

  else if (openingHours.rawDate.getTime() === utils.addDaysToDate(today, 2).getTime()) {
    currentStatus = i18n.t('Otevřeno bude opět pozítří v {0}', [trimZero(openingHours.times[0].from)]);
    currentStatusBg = 'bg-secondary-500';
    currentStatusColor = 'text-secondary-500';
  }

  // other days

  else {
    currentStatus = i18n.t('Otevřeno bude opět {0} v {1}', [i18n.t(onWeekDaysTranslations[openingHours.rawDate.getDay()]), trimZero(openingHours.times[0].from)]);
    currentStatusBg = 'bg-secondary-500';
    currentStatusColor = 'text-secondary-500';
  }
}
else {
  currentStatus = i18n.t('Dočasně zavřeno');
  currentStatusBg = 'bg-error-500';
  currentStatusColor = 'text-error-500';
}

</script>

<style lang="postcss">
.branch-opening {
  @apply mb-4 indent-0 tracking-normal;

  * {
    @apply !leading-5 !indent-0;
  }

  &__information {
    @apply py-4 text-error-500 tablet:px-4;

    &::before {
      @apply content-[''] block mb-2 max-w-[100px] border-b border-error-500;
    }

    p {
      @apply !mb-0;
    }
  }

  &__days {
    @apply flex flex-wrap justify-center gap-x-1 gap-y-4 mb-4 tablet:mt-4 desktop:gap-x-4;
  }

  &__day {

    @apply text-center;

    &__label {
      @apply flex flex-col justify-center items-center w-[96px] aspect-square p-2 text-lg border border-neutral-300 text-secondary-500;

      &__date {
        @apply mt-3 text-3xl font-bold text-neutral-500;
      }
    }

    &__times {
      @apply mt-1 text-[0.9rem];
    }

    &--closed {
      @apply text-neutral-300;

      .branch-opening__day__label {
        @apply text-neutral-300 border-neutral-300;

        &__date {
          @apply text-neutral-300;
        }
      }
    }

    &:first-of-type {
      @apply relative w-full flex justify-center tablet:w-auto;

      .branch-opening__day__wrapper {

        &::before {
          @apply absolute top-0 left-[calc(50%-0.7rem)] content-[''] border-neutral-50;
          border-left: 0.7rem solid transparent;
          border-right: 0.7rem solid transparent;
          border-top-width: 0.7rem;
        }
      }

      .branch-opening__day__label {
        @apply text-neutral-50 border-0 bg-secondary-500;

        &__date {
          @apply text-neutral-50;
        }
      }
    }

    &--closed:first-of-type {

      .branch-opening__day__label {
        @apply bg-neutral-300;
      }
    }
  }
}
</style>
